import React, { useState } from "react";
import { AlertTriangle } from "lucide-react";

import type { ShiftAssignmentBadge, ShiftAssignmentUiGroup } from "@/components/sections/ShiftActions.types.tsx";
import CollapsibleSection from "@/components/sections/CollapsibleSection.tsx";

const RadioButton: React.FC<{
    checked: boolean,
    onChange: React.ChangeEventHandler<HTMLInputElement> | undefined
}> = (props) => {
    return <input
        type="radio"
        checked={props.checked}
        disabled={false}
        readOnly
        onChange={props.onChange}
        className="h-4 w-4 accent-blue-600"
    />
}

const AssignmentBadge: React.FC<{ label: string; color: string }> = (props) => {
    return <span
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-white shrink-0"
        style={{ backgroundColor: props.color }}
    >
        <AlertTriangle size={12} />
        {props.label}
    </span>
}

const UserItem: React.FC<{
    selected: boolean
    id: string,
    label: string
    badges?: ShiftAssignmentBadge[]
    onSelect: (userId: string) => void
}> = (props) => {
    return <div className="flex items-center gap-2 p-2 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => { props.onSelect(props.id) }}
    >
        <RadioButton
            checked={props.selected}
            onChange={void 0}
        />
        <span className="text-sm flex-1 truncate">{props.label}</span>
        {props.badges && props.badges.length > 0 && (
            <span className="flex items-center gap-1 shrink-0">
                {props.badges.map(badge => (
                    <AssignmentBadge key={badge.key} label={badge.label} color={badge.color} />
                ))}
            </span>
        )}
    </div>
}

export type ShiftAssignmentListProps = {
    unassignItem: { id: string; name: string; selected: boolean };
    assignmentGroups: ShiftAssignmentUiGroup[];
    noEmployeesForRole: boolean;
    onSelect: (userId: string) => void;
};

const ShiftAssignmentList: React.FC<ShiftAssignmentListProps> = (props) => {
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set(["undesired", "leave", "unqualified"]));

    const toggleGroup = (key: string) => {
        setCollapsedGroups(prev => {
            const next = new Set(prev);

            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }

            return next;
        });
    };

    return (
        <>
            <UserItem
                key="none"
                onSelect={props.onSelect}
                id={props.unassignItem.id}
                label={props.unassignItem.name}
                selected={props.unassignItem.selected}
            />

            {props.noEmployeesForRole && (
                <div className="p-2 text-sm text-gray-500">Aucun employé qualifié pour ce rôle.</div>
            )}

            {props.assignmentGroups.map(group => {
                    if (group.items.length === 0) {
                        return null;
                    }

                    return (
                        <CollapsibleSection
                            key={group.key}
                            title={group.label}
                            count={group.items.length}
                            open={!collapsedGroups.has(group.key)}
                            onToggle={() => toggleGroup(group.key)}
                            headerClassName="text-xs font-semibold uppercase tracking-wide text-gray-500 py-1.5"
                            contentClassName="pt-0"
                        >
                            {group.items.map(item => (
                                <UserItem
                                    key={item.id}
                                    onSelect={props.onSelect}
                                    id={item.id}
                                    label={item.name}
                                    selected={item.selected}
                                    badges={item.badges}
                                />
                            ))}
                        </CollapsibleSection>
                    );
            })}
        </>
    );
};

export default ShiftAssignmentList;
